import { useState } from 'react'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs'
import { ErrorState } from '../components/ui/error-state'
import { LoadingSpinner } from '../components/ui/loading-states'
import { KpiCards } from '../components/pharma/KpiCards'
import { ExpiryRiskTab } from '../components/pharma/ExpiryRiskTab'
import { DeadStockTab } from '../components/pharma/DeadStockTab'
import { TurnoverTab } from '../components/pharma/TurnoverTab'
import { MovementsTab } from '../components/pharma/MovementsTab'
import {
  usePharmaDimensions,
  usePharmaKpis,
} from '../hooks/usePharmaAnalytics'
import { formatIsoDate } from '../utils/dateFormat'
import type {
  PharmaDeadStockFilters,
  PharmaExpiryRiskFilters,
  PharmaMovementsFilters,
  PharmaTurnoverFilters,
} from '../types/pharma-analytics.types'

const PAGE_SIZE = 50

/**
 * Фарм-аналитика: витрины поверх данных 1С — остатки, сроки годности, движения.
 *
 * Всё, что здесь показано, считается по одному источнику (1С). Спрос из DamuMed
 * появится в Фазе 2, когда будет сопоставлена номенклатура.
 */
export function PharmaAnalytics() {
  const [tab, setTab] = useState('overview')
  const [warehouse, setWarehouse] = useState<string>('all')
  const [includeNonClinical, setIncludeNonClinical] = useState(false)

  const warehouseFilter = warehouse === 'all' ? undefined : warehouse

  const {
    data: dimensions,
    isLoading: dimensionsLoading,
  } = usePharmaDimensions()

  const {
    data: kpis,
    isLoading: kpisLoading,
    error: kpisError,
    refetch: refetchKpis,
  } = usePharmaKpis({ warehouse: warehouseFilter })

  const [expiryFilters, setExpiryFilters] = useState<PharmaExpiryRiskFilters>({
    page: 1,
    limit: PAGE_SIZE,
    sort: 'expiry_asc',
  })
  const [deadStockFilters, setDeadStockFilters] =
    useState<PharmaDeadStockFilters>({
      page: 1,
      limit: PAGE_SIZE,
      windowDays: 60,
      sort: 'value_desc',
    })
  const [turnoverFilters, setTurnoverFilters] = useState<PharmaTurnoverFilters>({
    page: 1,
    limit: PAGE_SIZE,
    sort: 'external_out_desc',
  })
  const [movementsFilters, setMovementsFilters] =
    useState<PharmaMovementsFilters>({ page: 1, limit: PAGE_SIZE })

  // Фильтр склада общий для всех вкладок: держать его в каждой отдельно значило бы
  // ронять контекст при переключении.
  const withWarehouse = <T extends { warehouse?: string }>(filters: T): T => ({
    ...filters,
    warehouse: warehouseFilter,
  })

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Фарм-аналитика</h1>
          <p className="text-muted-foreground">
            Остатки, сроки годности и движения медикаментов по данным 1С.
            {dimensions?.asOf && (
              <> Срез остатков на {formatIsoDate(dimensions.asOf)}.</>
            )}
          </p>
        </div>

        <div className="min-w-[260px]">
          <Select value={warehouse} onValueChange={setWarehouse}>
            <SelectTrigger>
              <SelectValue placeholder="Все склады" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Все склады</SelectItem>
              {(dimensions?.warehouses ?? []).map((wh) => (
                <SelectItem key={wh.code} value={wh.code}>
                  {wh.name ?? wh.code}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {kpisLoading || dimensionsLoading ? (
        <div className="py-10">
          <LoadingSpinner />
        </div>
      ) : kpisError ? (
        <ErrorState
          title="Не удалось загрузить показатели"
          description={(kpisError as Error)?.message}
          onAction={() => void refetchKpis()}
        />
      ) : kpis && !kpis.asOf ? (
        <div className="rounded-md border border-border bg-muted/40 px-4 py-6 text-center text-muted-foreground">
          Срезов остатков ещё нет. Данные появятся после первой синхронизации с 1С.
        </div>
      ) : (
        kpis && (
          <KpiCards
            data={kpis}
            includeNonClinical={includeNonClinical}
            onToggleNonClinical={setIncludeNonClinical}
          />
        )
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="overview">Риск списания</TabsTrigger>
          <TabsTrigger value="dead">Мёртвый запас</TabsTrigger>
          <TabsTrigger value="turnover">Оборот и закупки</TabsTrigger>
          <TabsTrigger value="movements">Движения</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-4">
          <ExpiryRiskTab
            filters={withWarehouse(expiryFilters)}
            onFiltersChange={setExpiryFilters}
          />
        </TabsContent>

        <TabsContent value="dead" className="mt-4">
          <DeadStockTab
            filters={withWarehouse(deadStockFilters)}
            onFiltersChange={setDeadStockFilters}
          />
        </TabsContent>

        <TabsContent value="turnover" className="mt-4">
          <TurnoverTab
            filters={withWarehouse(turnoverFilters)}
            onFiltersChange={setTurnoverFilters}
          />
        </TabsContent>

        <TabsContent value="movements" className="mt-4">
          <MovementsTab
            filters={withWarehouse(movementsFilters)}
            onFiltersChange={setMovementsFilters}
            operationTypes={dimensions?.operationTypes ?? []}
          />
        </TabsContent>
      </Tabs>
    </div>
  )
}
